import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api";
import { MailCheck, RotateCcw } from "lucide-react";

export function EmailOtpStep({ challenge, onVerify, onResend, onCancel, title = "Verify your sign-in" }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(challenge?.resend_in_seconds || 0);
  useEffect(() => {
    setResendIn(challenge?.resend_in_seconds || 0);
    const timer = window.setInterval(() => setResendIn((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [challenge]);
  const verify = async (event) => {
    event.preventDefault();
    setBusy(true);
    try { await onVerify(code); }
    catch (error) { toast.error(getApiErrorMessage(error)); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    setBusy(true);
    try {
      const next = await onResend();
      setCode(""); setResendIn(next?.resend_in_seconds || 60);
      toast.success("A new verification code was sent");
    } catch (error) { toast.error(getApiErrorMessage(error)); }
    finally { setBusy(false); }
  };
  return <form className="mt-6 space-y-4" onSubmit={verify} data-testid="email-otp-step">
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
      <div className="flex items-center gap-2 font-medium"><MailCheck className="h-4 w-4" />{title}</div>
      <p className="mt-1 text-xs">Enter the six-digit code sent to <b>{challenge.masked_email}</b>. It expires in {Math.max(1, Math.ceil((challenge.expires_in_seconds || 300) / 60))} minutes.</p>
    </div>
    <div className="space-y-1.5"><Label htmlFor="email-otp">Verification code</Label><Input id="email-otp" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} className="text-center font-mono text-xl tracking-[0.45em]" data-testid="email-otp-input" /></div>
    <Button type="submit" disabled={busy || code.length !== 6} className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="email-otp-verify">{busy ? "Verifying…" : "Verify and sign in"}</Button>
    <div className="flex flex-col gap-3 text-xs min-[380px]:flex-row min-[380px]:items-center min-[380px]:justify-between"><button type="button" onClick={onCancel} className="min-h-11 text-left text-[var(--ink-muted)] hover:underline min-[380px]:min-h-0">Use another account</button><button type="button" onClick={resend} disabled={busy || resendIn > 0} className="flex min-h-11 items-center gap-1 font-medium text-[var(--cta)] disabled:text-[var(--ink-muted)] min-[380px]:min-h-0"><RotateCcw className="h-3.5 w-3.5" />{resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}</button></div>
  </form>;
}
